import moment from "moment";
import "moment/locale/pt-br";
import extenso from "extenso";

export const RECEIPT_PRINT_CSS = `
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; width: 100%; display: flex; flex-direction: column; margin: 0px; padding: 0px; }
  img { max-height: 35px !important; min-height: 35px !important; }
  .font-head-title { font-size: 14pt; font-weight: bold; font-family: 'Arsenal', sans-serif; color: #333; }
  .font-head-subtitle { font-size: 12pt; font-family: 'Arsenal', sans-serif; color: #666; }
  .font-title { font-size: 20pt; font-weight: bold; font-family: 'Chakra Petch', sans-serif; color: #333; }
  .receipt-copy { min-height: 50%; display: flex; padding-right: 30px !important; flex-direction: column; flex-wrap: nowrap; max-width: 96%; }
  .receipt-copy.first { margin-bottom: 30px; padding-bottom: 30px; border-bottom: 1px dashed #333; }
  .receipt-header { border-radius: 6px; border: 12px solid #e5e5e5; margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; flex-direction: row; flex-wrap: nowrap; width: 100%; }
  .receipt-tenant { display: flex; align-items: center; flex-direction: column; padding-right: 7px; padding-left: 7px; }
  .receipt-number { border-left: 12px solid #e5e5e5; padding: 3px; flex-direction: column; display: flex; width: 20%; align-items: center; justify-content: space-between; }
  .receipt-number-line { flex-wrap: nowrap; display: flex; justify-content: space-between; }
  .receipt-body { border-radius: 6px; border: 12px solid #e5e5e5; padding: 12px; display: flex; min-width: 97%; max-width: 97%; flex-direction: column; }
  .receipt-field { margin-bottom: 12px; display: flex; flex-direction: column; }
  .receipt-field-label { font-size: 9pt; }
  .receipt-sign-wrapper { width: 100%; margin-top: 12px !important; flex-wrap: nowrap; display: flex; flex-direction: column; }
  .receipt-sign { display: flex; justify-content: center; align-items: center; flex-direction: column; width: 100%; background: #e5e5e5; border-radius: 6px; padding: 12px; padding-top: 30px !important; }
  @page { size: a4; margin: 0.5cm; }
`;

function escapeHtml(value) {
  return String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function toNumber(value) {
  if (typeof value === "number") return isFinite(value) ? value : 0;
  if (!value) return 0;
  const parsed = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
  return isFinite(parsed) ? parsed : 0;
}

export function formatMoney(value) {
  return toNumber(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function valueToExtense(value) {
  const amount = toNumber(value);
  try {
    return extenso(amount.toFixed(2).replace(".", ","), {
      mode: "currency",
      currency: { type: "BRL" }
    });
  } catch (error) {
    return formatMoney(amount);
  }
}

function tenantFromUser(user = {}) {
  const tenant = Array.isArray(user.tenant) ? user.tenant[0] : user.tenant;
  return tenant || {};
}

function clientNameFromSale(sale = {}) {
  const compra = sale.compra || {};
  const cliente = compra.cliente || {};
  return cliente.nome || "Cliente nao informado";
}

/**
 * Monta os dados do recibo de um pagamento, tanto para pagamentos novos
 * (que ja gravam `receipt`) quanto para os historicos que nao tem recibo salvo.
 */
export function normalizeReceipt({ sale = {}, payment = {}, index = 0 } = {}) {
  const stored = payment.receipt || {};
  const value = toNumber(stored.value || payment.valor);
  const date = stored.data || payment.data || new Date();
  return {
    order: stored.order || payment.receipt_order || index + 1,
    client: stored.client || clientNameFromSale(sale),
    value,
    value_extense: stored.value_extense || valueToExtense(value),
    referrer_to:
      stored.referrer_to ||
      `Pagamento em ${payment.forma_de_pagamento || "dinheiro"} da venda #${sale.id_transacao || ""}`.trim(),
    data: stored.data_extense || moment(date).locale("pt-br").format("LLLL"),
    date
  };
}

function receiptCopy({ sale, receipt, tenant, copyClass }) {
  const logo = tenant.profileImage
    ? `<img src="${escapeHtml(tenant.profileImage)}" alt="" />`
    : "";
  const address =
    tenant.address && tenant.address.formatted_address
      ? `<span class="font-head-subtitle">${escapeHtml(tenant.address.formatted_address)}</span>`
      : "";
  const phone = tenant.phone
    ? `<span class="font-head-subtitle">${escapeHtml(tenant.phone)}</span>`
    : "";

  return `
    <div class="receipt-copy ${copyClass}">
      <div class="receipt-header">
        ${logo}
        <div class="receipt-tenant">
          <span class="font-head-title">${escapeHtml(tenant.nome || "")}</span>
          <span class="font-head-subtitle">CNPJ: ${escapeHtml(tenant.cnpj || "")}</span>
          ${address}
          ${phone}
        </div>
        <div class="font-head-subtitle receipt-number">
          <div class="receipt-number-line">
            <span class="font-head-title" style="margin-right: 3px;">RECIBO </span>
            <span class="font-head-subtitle" style="font-size: 9pt !important;">N&ordm; ${escapeHtml(sale.id_transacao || "")}-${escapeHtml(receipt.order)} </span>
          </div>
          <span class="font-head-title">${escapeHtml(formatMoney(receipt.value))}</span>
        </div>
      </div>
      <div class="receipt-body">
        <div class="receipt-field">
          <span class="font-head-subtitle receipt-field-label">Recebi(emos) de:</span>
          <span class="font-head-title">${escapeHtml(receipt.client)}</span>
        </div>
        <div class="receipt-field">
          <span class="font-head-subtitle receipt-field-label">A quantia de:</span>
          <span class="font-head-title">${escapeHtml(receipt.value_extense)}</span>
        </div>
        <div class="receipt-field">
          <span class="font-head-subtitle receipt-field-label">Referente a:</span>
          <span class="font-head-title">${escapeHtml(receipt.referrer_to)}</span>
        </div>
      </div>
      <div class="receipt-sign-wrapper">
        <div class="receipt-sign">
          <div style="margin-top: 20px;">__________________________________________________</div>
          <div>Assinatura</div>
          <span style="margin-top: 18px !important;" class="font-head-title">${escapeHtml(
            moment(receipt.date).format("DD/MM/YYYY")
          )}</span>
          <span style="margin-top: 3px; font-size: 9pt;" class="font-head-subtitle">${escapeHtml(receipt.data)}</span>
        </div>
      </div>
    </div>
  `;
}

/**
 * Gera o HTML completo do recibo (duas vias) sem depender de nenhum node
 * renderizado na tela, para que a impressao funcione sempre.
 */
export function buildReceiptHtml({ sale = {}, receipt = {}, user = {}, tenant } = {}) {
  const tenantData = tenant || tenantFromUser(user);
  return `
    <div id="print-receipt" style="width: 95%; display: flex; flex-direction: column;">
      ${receiptCopy({ sale, receipt, tenant: tenantData, copyClass: "first" })}
      ${receiptCopy({ sale, receipt, tenant: tenantData, copyClass: "second" })}
    </div>
  `;
}
